import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { getAccountStatus } from '@helpers/Accounts/getAccountStatus';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';

export type TUseRCFWithdrawFunds = {
    outstandingBalance: number;
    formalOfferAccessPeriod: NonNullable<
        TAccount['formal_offer_access_period']
    >;
    isOverdue: boolean;
};

export const useRCFWithdrawFunds = (
    accountId: string
): TUseRCFWithdrawFunds => {
    const stateKey = useStateKey();
    const { data: account } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: ({ data }) =>
            data.accounts.find((account) => account.account_id === accountId),
        suspense: true
    });

    if (!account) {
        const errorMessage = `<RCFWithdrawFunds /> - RCF Account with id ${accountId} not found`;
        logError(errorMessage, { tags: TAGS.DE_LENDING });
        throw new Error(errorMessage);
    }

    const formalOfferAccessPeriod = account.formal_offer_access_period;

    if (!formalOfferAccessPeriod) {
        const errorMessage = `<RCFWithdrawFunds /> - RCF Account with id ${accountId} has no access period`;
        logError(errorMessage, { tags: TAGS.DE_LENDING });
        throw new Error(errorMessage);
    }

    const outstandingBalance = account.pending_balance!.total!;
    const isOverdue = getAccountStatus(account) === 'overdue';

    return { outstandingBalance, formalOfferAccessPeriod, isOverdue };
};
